// MerchantProDashboard — v2026-07-02 "drapeau CIV premium" + données réelles.
// ─────────────────────────────────────────────────────────────────────────────
// Bento 2×2 KPIs + mini-chart revenu hebdo + dernières commandes + CTA champagne.
//
// DATA-DRIVEN (plus aucun chiffre inventé côté frontend) :
//   - Wallet pro    : bank.balance_fcfa (réel, /me/pro/bank)
//   - Commandes     : GET /api/v3/me/pro/orders (pending+assigned+en_route)
//   - CA du jour    : somme des commandes delivered dont delivered_at = aujourd'hui
//   - Revenu hebdo  : série 7 jours construite depuis delivered_at
//   - Note          : aucun backend reviews → "—" honnête (pas de 4.8 fictif)
//   États vides élégants quand le compte n'a pas encore d'activité.
//   NB : /me/pro/orders sert un mock CÔTÉ SERVEUR pour le compte commerçant
//   test (+2250700000777) — documenté proRoutes.js, hors périmètre frontend.
//
// Palette : panneau émeraude (drapeau CIV premium) + actions champagne gold.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from "react";
import { m } from "framer-motion";
import { AyaCountUp } from "@/components/ui/CountUp";
import { AyaConfettiBurst } from "@/components/ui/ConfettiBurst";
import AyaAsset from "@/components/ui/AyaAsset";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "civ_user_token";

const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const STATUS_PILL = {
  delivered: { bg: "rgba(16,185,129,0.15)", color: "#34D399", border: "rgba(16,185,129,0.4)", label: "Livrée" },
  en_route: { bg: "rgba(59,130,246,0.15)", color: "#60A5FA", border: "rgba(59,130,246,0.4)", label: "En route" },
  assigned: { bg: "rgba(59,130,246,0.12)", color: "#93C5FD", border: "rgba(59,130,246,0.35)", label: "Assignée" },
  pending: { bg: "rgba(232,213,163,0.15)", color: "#EBD9A8", border: "rgba(232,213,163,0.45)", label: "À dispatcher" },
  // v3.8.0 — statuts réels du cycle commerçant
  confirmed: { bg: "rgba(16,185,129,0.18)", color: "#6EE7B7", border: "rgba(16,185,129,0.45)", label: "🆕 Nouvelle" },
  merchant_accepted: { bg: "rgba(232,213,163,0.18)", color: "#EBD9A8", border: "rgba(232,213,163,0.45)", label: "Acceptée" },
  dispatch_pending: { bg: "rgba(232,213,163,0.15)", color: "#FBBF24", border: "rgba(251,191,36,0.4)", label: "Recherche livreur" },
  courier_assigned: { bg: "rgba(59,130,246,0.15)", color: "#60A5FA", border: "rgba(59,130,246,0.4)", label: "Livreur en route" },
  dispatched: { bg: "rgba(59,130,246,0.15)", color: "#60A5FA", border: "rgba(59,130,246,0.4)", label: "Livreur en route" },
  dispatch_expired: { bg: "rgba(239,68,68,0.15)", color: "#FCA5A5", border: "rgba(239,68,68,0.4)", label: "Échec livreur" },
  failed: { bg: "rgba(239,68,68,0.15)", color: "#FCA5A5", border: "rgba(239,68,68,0.4)", label: "Échec" },
};

// v3.8.0 — Cycle du bouton unique : Accepter → Faire livrer → En cours → Livré.
function orderCycle(status) {
  if (status === "delivered") return { label: "✓ Livré", disabled: true, done: true };
  if (status === "confirmed") return { label: "✅ Accepter la commande", action: "accept" };
  if (status === "merchant_accepted") return { label: "🛵 Faire livrer", action: "dispatch" };
  if (status === "dispatch_expired" || status === "failed") return { label: "🔁 Relancer livreur", action: "dispatch" };
  if (["dispatch_pending", "courier_assigned", "dispatched"].includes(status)) return { label: "🚚 En cours de livraison", disabled: true };
  return null;
}

function fmtFcfa(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return new Intl.NumberFormat("fr-FR").format(Math.round(n)) + " FCFA";
}

function _isSameDay(iso, ref) {
  if (!iso) return false;
  const d = new Date(iso);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth() && d.getDate() === ref.getDate();
}

// ─── Mini-chart SVG inline (linéaire, pas de dep recharts) ───────────────────
function MiniChart({ values, labels, width = 320, height = 80, ariaLabel }) {
  const { path, areaPath, points, max } = useMemo(() => {
    const padX = 8;
    const padY = 8;
    const w = width - padX * 2;
    const h = height - padY * 2;
    const mx = Math.max(...values, 1);
    const step = w / Math.max(values.length - 1, 1);
    const pts = values.map((v, i) => {
      const x = padX + i * step;
      const y = padY + h - (v / mx) * h;
      return { x, y };
    });
    const p = pts
      .map((pt, i) => `${i === 0 ? "M" : "L"} ${pt.x.toFixed(2)} ${pt.y.toFixed(2)}`)
      .join(" ");
    const area =
      p + ` L ${pts[pts.length - 1].x.toFixed(2)} ${height - padY} L ${pts[0].x.toFixed(2)} ${height - padY} Z`;
    return { path: p, areaPath: area, points: pts, max: mx };
  }, [values, width, height]);

  return (
    <svg
      data-testid="merchant-mini-chart"
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel || "Revenu hebdomadaire"}
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id="merchant-chart-grad-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34D399" stopOpacity="0.32" />
          <stop offset="100%" stopColor="#E4C87F" stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="merchant-chart-grad-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#E4C87F" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#merchant-chart-grad-area)" />
      <path
        d={path}
        fill="none"
        stroke="url(#merchant-chart-grad-line)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={i === points.length - 1 ? 3.4 : 2.2}
          fill={i === points.length - 1 ? "#E4C87F" : "#10b981"}
          style={i === points.length - 1 ? { filter: "drop-shadow(0 0 4px rgba(228,200,127,0.65))" } : undefined}
        />
      ))}
      {labels && (
        <g aria-hidden="true">
          {points.map((p, i) => (
            <text
              key={i}
              x={p.x}
              y={height - 1}
              fontSize="8"
              fill="rgba(244,241,232,0.45)"
              textAnchor="middle"
            >
              {labels[i]}
            </text>
          ))}
        </g>
      )}
      <title>{`Max : ${fmtFcfa(max)}`}</title>
    </svg>
  );
}

// ─── KPI Card ────────────────────────────────────────────────────────────────
function KpiCard({
  testId,
  label,
  value,
  suffix = "",
  prefix = "",
  decimals = 0,
  accent = "emerald",
  icon,
  delay = 0,
  asNumber = true,
  sub = null,
}) {
  const palettes = {
    emerald: {
      bg: "linear-gradient(135deg, rgba(16,185,129,0.20), rgba(16,185,129,0.05))",
      border: "rgba(52,211,153,0.35)",
      glow: "0 0 0 1px rgba(52,211,153,0.10), 0 8px 28px rgba(16,185,129,0.18)",
      accent: "#34D399",
    },
    champagne: {
      bg: "linear-gradient(135deg, rgba(228,200,127,0.22), rgba(205,164,82,0.06))",
      border: "rgba(232,213,163,0.42)",
      glow: "0 0 0 1px rgba(232,213,163,0.12), 0 8px 28px rgba(205,164,82,0.20)",
      accent: "#EBD9A8",
    },
    ivory: {
      bg: "linear-gradient(135deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))",
      border: "rgba(244,241,232,0.18)",
      glow: "0 0 0 1px rgba(244,241,232,0.06), 0 8px 28px rgba(0,0,0,0.25)",
      accent: "#F4F1E8",
    },
  };
  const p = palettes[accent] || palettes.ivory;
  return (
    <m.div
      data-testid={testId}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.42, delay, ease: [0.32, 0.72, 0, 1] }}
      whileHover={{ y: -2 }}
      style={{
        position: "relative",
        background: p.bg,
        border: `1px solid ${p.border}`,
        borderRadius: 16,
        padding: "16px 16px 14px",
        boxShadow: p.glow,
        backdropFilter: "blur(var(--aya-glass-blur-sm))",
        WebkitBackdropFilter: "blur(var(--aya-glass-blur-sm))",
        cursor: "default",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: 10,
            background: `${p.accent}22`,
            color: p.accent,
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {icon}
        </span>
        <span
          style={{
            fontSize: 11,
            color: "rgba(244,241,232,0.62)",
            letterSpacing: 0.6,
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          {label}
        </span>
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: p.accent,
          letterSpacing: -0.4,
          fontFeatureSettings: "'tnum'",
          lineHeight: 1.1,
        }}
      >
        {asNumber ? (
          <AyaCountUp
            key={`kpi-${Number(value) || 0}`}
            value={Number(value) || 0}
            duration={900}
            decimals={decimals}
            prefix={prefix}
            suffix={suffix}
            locale="fr-FR"
          />
        ) : (
          <span>{prefix}{value}{suffix}</span>
        )}
      </div>
      {sub && (
        <div style={{ fontSize: 9.5, color: "rgba(244,241,232,0.45)", marginTop: 4 }}>{sub}</div>
      )}
    </m.div>
  );
}

// ─── Composant principal ─────────────────────────────────────────────────────
export default function MerchantProDashboard({ bank }) {
  const confettiRef = useRef(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoaded, setOrdersLoaded] = useState(false);

  // Fetch commandes réelles (v3.8.0 : /merchant/orders + polling 8s pour suivre le livreur).
  const fetchOrders = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setOrdersLoaded(true); return; }
    try {
      const r = await fetch(`${API}/api/v3/merchant/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json().catch(() => ({}));
      if (j.ok && Array.isArray(j.orders)) setOrders(j.orders);
    } catch (_) { /* silencieux — états vides */ }
    finally { setOrdersLoaded(true); }
  };
  useEffect(() => {
    fetchOrders();
    const iv = setInterval(fetchOrders, 8000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v3.8.0 — Action du bouton cyclique (accept | dispatch).
  const [actionBusy, setActionBusy] = useState(null);
  const doOrderAction = async (orderId, action) => {
    if (actionBusy) return;
    setActionBusy(orderId);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const r = await fetch(`${API}/api/v3/merchant/orders/${orderId}/${action}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) alert(`Échec : ${j.error || "erreur"}`);
      await fetchOrders();
    } catch (_) { alert("Erreur réseau."); }
    finally { setActionBusy(null); }
  };

  // Confetti subtil au mount (600ms après pour laisser le contenu s'afficher).
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        confettiRef.current?.fire({
          particleCount: 40,
          spread: 60,
          startVelocity: 28,
          origin: { x: 0.5, y: 0.3 },
        });
      } catch (_e) { /* noop */ }
    }, 600);
    return () => clearTimeout(t);
  }, []);

  // ─── KPIs calculés depuis les données réelles ──────────────────────────
  const { caJour, commandesActives, hebdo, hebdoLabels, hebdoTotal, recentOrders } = useMemo(() => {
    const now = new Date();
    const delivered = orders.filter((o) => o.status === "delivered");
    const actives = orders.filter((o) => ["pending", "assigned", "en_route", "confirmed", "merchant_accepted", "dispatch_pending", "courier_assigned", "dispatched"].includes(o.status));

    const ca = delivered
      .filter((o) => _isSameDay(o.delivered_at || o.created_at, now))
      .reduce((s, o) => s + (Number(o.amount_fcfa) || 0), 0);

    // Série 7 jours (J-6 → aujourd'hui) depuis delivered_at.
    const series = [];
    const labels = [];
    for (let i = 6; i >= 0; i -= 1) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      labels.push(JOURS[(d.getDay() + 6) % 7]);
      series.push(
        delivered
          .filter((o) => _isSameDay(o.delivered_at, d))
          .reduce((s, o) => s + (Number(o.amount_fcfa) || 0), 0)
      );
    }

    const recent = [...orders]
      .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))
      .slice(0, 8);

    return {
      caJour: ca,
      commandesActives: actives.length,
      hebdo: series,
      hebdoLabels: labels,
      hebdoTotal: series.reduce((a, b) => a + b, 0),
      recentOrders: recent,
    };
  }, [orders]);

  const walletBalance = Number.isFinite(Number(bank?.balance_fcfa)) ? Number(bank.balance_fcfa) : 0;

  return (
    <m.section
      data-testid="merchant-pro-dashboard"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
      aria-label="Tableau de bord commerçant"
    >
      {/* Header commerçant — nom réel du compte (plus de "Tantie Adjoua" en dur) */}
      <div
        data-testid="merchant-illust-header"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "12px 14px",
          borderRadius: 18,
          background: "linear-gradient(135deg, rgba(6,42,31,0.65), rgba(13,79,55,0.35))",
          border: "1px solid rgba(232,213,163,0.35)",
          boxShadow: "var(--aya-shadow-glow-gold)",
          backdropFilter: "blur(var(--aya-glass-blur-md))",
          WebkitBackdropFilter: "blur(var(--aya-glass-blur-md))",
        }}
      >
        <div style={{ width: 80, height: 80, borderRadius: 16, overflow: "hidden", flexShrink: 0, boxShadow: "0 8px 22px rgba(0,0,0,0.35)" }}>
          <AyaAsset name="merchant.tantie_adjoua" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="aya-text-display" style={{ fontSize: 20, color: "#F4F1E8", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {bank?.account_holder || "Mon commerce"}
            </span>
            <span aria-label="vérifié" title="Commerçant vérifié" style={{ fontSize: 15, flexShrink: 0 }}>✅</span>
          </div>
          <div style={{ fontSize: 11, color: "rgba(244,241,232,0.62)", letterSpacing: 0.4, textTransform: "uppercase", marginTop: 2 }}>
            Compte Pro Commerçant · {bank?.bank || "Bridge Bank Group"}
          </div>
        </div>
      </div>

      {/* Bento 2×2 KPIs — données réelles */}
      <div
        data-testid="merchant-bento-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 10,
        }}
      >
        <KpiCard
          testId="kpi-ca-jour"
          label="CA du jour"
          value={caJour}
          suffix=" F"
          accent="emerald"
          icon="₣"
          delay={0.05}
          sub={ordersLoaded && caJour === 0 ? "livraisons du jour" : null}
        />
        <KpiCard
          testId="kpi-commandes"
          label="Commandes"
          value={commandesActives}
          accent="ivory"
          icon="📦"
          delay={0.10}
          sub="en cours"
        />
        <KpiCard
          testId="kpi-note"
          label="Note"
          value="—"
          asNumber={false}
          accent="champagne"
          icon="★"
          delay={0.15}
          sub="pas encore d'avis clients"
        />
        <KpiCard
          testId="kpi-wallet"
          label="Wallet pro"
          value={walletBalance}
          suffix=" F"
          accent="champagne"
          icon="💳"
          delay={0.20}
        />
      </div>

      {/* Chart hebdo — réel, ou état vide élégant */}
      <m.div
        data-testid="merchant-chart-card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.42, delay: 0.28, ease: [0.32, 0.72, 0, 1] }}
        className="aya-pro-chart"
        style={{
          background: "linear-gradient(135deg, rgba(6,42,31,0.55), rgba(13,79,55,0.30))",
          border: "1px solid rgba(232,213,163,0.25)",
          borderRadius: 16,
          padding: "14px 16px 8px",
          boxShadow: "0 8px 28px rgba(0,0,0,0.25)",
          backdropFilter: "blur(var(--aya-glass-blur-sm))",
          WebkitBackdropFilter: "blur(var(--aya-glass-blur-sm))",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 6,
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: "rgba(244,241,232,0.62)",
              letterSpacing: 0.6,
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Revenu cette semaine
          </span>
          <span
            data-testid="merchant-chart-total"
            style={{
              fontSize: 12,
              color: "#EBD9A8",
              fontWeight: 700,
              fontFeatureSettings: "'tnum'",
            }}
          >
            {fmtFcfa(hebdoTotal)}
          </span>
        </div>
        {hebdoTotal > 0 ? (
          <MiniChart values={hebdo} labels={hebdoLabels} height={120} ariaLabel="Revenu hebdomadaire" />
        ) : (
          <div
            data-testid="merchant-chart-empty"
            style={{
              height: 120,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              color: "rgba(244,241,232,0.5)",
              fontSize: 12.5,
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 22 }} aria-hidden="true">📈</span>
            Tes revenus s&apos;afficheront ici dès tes premières livraisons de la semaine.
          </div>
        )}
      </m.div>

      {/* Dernières commandes — réelles, ou état vide élégant */}
      <m.div
        data-testid="merchant-recent-orders"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.42, delay: 0.34, ease: [0.32, 0.72, 0, 1] }}
        style={{ display: "flex", flexDirection: "column", gap: 6 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            padding: "0 2px",
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: "rgba(244,241,232,0.62)",
              letterSpacing: 0.6,
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Dernières commandes
          </span>
        </div>
        {recentOrders.length === 0 ? (
          <div
            data-testid="merchant-recent-orders-empty"
            style={{
              padding: "18px 14px",
              background: "rgba(6,42,31,0.45)",
              border: "1px dashed rgba(232,213,163,0.30)",
              borderRadius: 12,
              fontSize: 12.5,
              color: "rgba(244,241,232,0.55)",
              textAlign: "center",
              lineHeight: 1.5,
            }}
          >
            {ordersLoaded
              ? "Aucune commande pour le moment — elles apparaîtront ici en temps réel."
              : "Chargement des commandes…"}
          </div>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {recentOrders.map((o, idx) => {
              const pill = STATUS_PILL[o.status] || STATUS_PILL.pending;
              const cycle = orderCycle(o.status);
              return (
                <m.li
                  key={o.order_id || o.order_ref || idx}
                  data-testid={`merchant-recent-order-${idx}`}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.28,
                    delay: 0.40 + idx * 0.04,
                    ease: [0.32, 0.72, 0, 1],
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    padding: "10px 12px",
                    background: "rgba(6,42,31,0.45)",
                    border: "1px solid rgba(244,241,232,0.08)",
                    borderRadius: 12,
                    fontSize: 12,
                    color: "#F4F1E8",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      style={{
                        fontFamily: "ui-monospace, monospace",
                        fontSize: 10,
                        color: "rgba(244,241,232,0.5)",
                        flex: "0 0 auto",
                      }}
                    >
                      {o.order_ref}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {o.item_summary || o.client_pseudo || "Client AYA"}
                      </div>
                      <div style={{ fontSize: 10, color: "rgba(244,241,232,0.55)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {o.courier_name ? `🛵 ${o.courier_name}` : (o.drop || o.pickup || "")}
                      </div>
                    </div>
                    <span
                      style={{
                        fontWeight: 700,
                        color: "#EBD9A8",
                        fontFeatureSettings: "'tnum'",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmtFcfa(o.amount_fcfa)}
                    </span>
                    <span
                      data-testid={`merchant-recent-order-status-${idx}`}
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        padding: "2px 7px",
                        borderRadius: 999,
                        background: pill.bg,
                        color: pill.color,
                        border: `1px solid ${pill.border}`,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {pill.label}
                    </span>
                  </div>
                  {cycle && (
                    <button
                      type="button"
                      data-testid={`merchant-order-action-${idx}`}
                      disabled={cycle.disabled || actionBusy === o.order_id}
                      onClick={() => cycle.action && doOrderAction(o.order_id, cycle.action)}
                      style={{
                        alignSelf: "flex-end",
                        padding: "7px 14px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 800,
                        cursor: cycle.disabled ? "default" : "pointer",
                        border: cycle.done ? "1px solid rgba(16,185,129,0.5)" : "none",
                        background: cycle.done
                          ? "rgba(16,185,129,0.15)"
                          : cycle.disabled
                            ? "rgba(96,165,250,0.15)"
                            : cycle.action === "accept"
                              ? "linear-gradient(135deg, #F5E9C6, #CDA452)"
                              : "linear-gradient(135deg, #10B981, #059669)",
                        color: cycle.done ? "#34D399" : cycle.disabled ? "#93C5FD" : cycle.action === "accept" ? "#3A2E10" : "#fff",
                        opacity: actionBusy === o.order_id ? 0.6 : 1,
                        transition: "background 200ms ease, color 200ms ease",
                      }}
                    >
                      {actionBusy === o.order_id ? "…" : cycle.label}
                    </button>
                  )}
                </m.li>
              );
            })}
          </ul>
        )}
      </m.div>

      {/* CTA champagne */}
      <m.button
        type="button"
        data-testid="merchant-cta-configure"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.42, delay: 0.62, ease: [0.32, 0.72, 0, 1] }}
        whileHover={{ y: -1, scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => {
          // 2026-07-08 (owner) — ouvre l'éditeur de mini-site (Gérer ma boutique).
          window.dispatchEvent(new CustomEvent("aya:open-minisite-editor"));
        }}
        style={{
          marginTop: 4,
          padding: "12px 18px",
          background: "linear-gradient(135deg, #F5E9C6 0%, #E4C87F 55%, #CDA452 100%)",
          color: "#3A2E10",
          border: "1px solid rgba(232,213,163,0.5)",
          borderRadius: 12,
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: 0.3,
          cursor: "pointer",
          boxShadow: "0 8px 22px rgba(205,164,82,0.30)",
        }}
        title="Gérer ma boutique (mini-site, produits, promos)"
      >
        ✨ Configurer ma boutique
      </m.button>

      {/* Confetti controller (invisible) */}
      <AyaConfettiBurst
        ref={confettiRef}
        palette="emerald-gold"
        particleCount={40}
        spread={60}
        startVelocity={28}
      />
    </m.section>
  );
}
